import { useState } from 'react'
import { ButtonKind, PanoramaButton, PanoramaSelectMenu } from 'shared-ui/components'
import { SpriteMap16 } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'
import { isNonNil } from 'utils'

type CFF = 'client' | 'farm' | 'field'

const NEW_OPTION_ID = -1

export const DropDownWithInput = ({
  type,
  onChange,
  options,
  placeholder,
  disabled = false,
  validEmpty,
  value
}: {
  validEmpty: boolean
  placeholder: string
  options: string[]
  type: CFF
  onChange: (val?: string) => void
  disabled?: boolean
  value: string | undefined
}) => {
  const { t } = useLocalization()
  const selectOptions = options.map((opt, i) => ({ name: opt, id: i }))
  const newLabel = t(($) => $.new)

  const [dropDownValue, setDropDownValue] = useState<{
    name: string
    id: number
  }>()

  const open = dropDownValue?.id === NEW_OPTION_ID

  return (
    <>
      <PanoramaSelectMenu
        invalid={!(validEmpty || (isNonNil(value) && value.length > 0))}
        key={type}
        label={t(($) => $.agConcepts[type], {
          count: 1
        })}
        options={selectOptions}
        value={isNonNil(value) ? dropDownValue : undefined}
        placeholder={placeholder}
        onChange={({ name, id }) => {
          setDropDownValue({ name, id })
          onChange(name)
        }}
        actions={({ close }) => (
          <PanoramaButton
            kind={ButtonKind.Secondary}
            icon={SpriteMap16.AddCircle16}
            className="w-full"
            onClick={() => {
              close()
              setDropDownValue({ name: newLabel, id: NEW_OPTION_ID })
              onChange('')
            }}
          >
            {newLabel}
          </PanoramaButton>
        )}
        disabled={disabled}
      />
      {open && (
        <StyledInput
          valid={Boolean(value?.length)}
          onChange={({ target }) => onChange(target.value)}
          placeholder="New Name"
        />
      )}
    </>
  )
}

const styledInputClasses = createVariants({
  base: 'rounded-lg outline-green-500 ui-disabled:cursor-not-allowed flex w-full max-w-full items-center justify-between border px-3 py-2 min-h-10 placeholder:pan-detail placeholder:italic placeholder:text-gray-400',
  variants: {
    valid: {
      false: 'outline outline-offset-2 outline-red-600'
    }
  },
  defaultVariants: { valid: true }
})

export const StyledInput: React.FC<React.ComponentPropsWithRef<'input'> & { valid?: boolean }> = ({
  valid,
  className,
  ...props
}) => <input className={styledInputClasses({ valid, className })} {...props} />
